// ============================================================
// useScenes - the GM's verbs over the wings, and the cut itself.
//
// The table is always exactly one scene, the one on stage; the others
// wait in the wings as GM-only documents (engine/scenes/types.ts). This
// hook owns the listener on the scene records and every verb the rail
// offers. The pure half - what a cut IS - is `engine/scenes`; this is
// the order the writes happen in, which is the whole of the risk:
//
//   PARK BEFORE STRIKE. The current stage is written to the wings
//   completely before any live document is touched, so a failure loses
//   nothing - the table is untouched and the wings hold a copy.
//
//   TOKENS BEFORE THE ROSTER. `useAutoPlacedTokens` rings anybody on
//   the roster with no token; writing the entering marks first means
//   it sees token and roster in the same render and rings nobody.
//
//   THE HOOK RINGS ITS OWN TRAVELLERS. A traveller stays on the roster
//   across the cut, and the arrival ledger is pruned only on departure,
//   so a traveller with no mark on the target would never be ringed by
//   the arrival effect - `deploy()` is called here instead.
//
//   THE PHASE AFTER THE RAISE (v8.430.0). A scene may open in a phase
//   of its own; it is set once its people are on the table.
//
//   THE STAGE DOCUMENT LAST. It is the commit point every client reacts
//   to: the title card, the curtain, the rail's ON STAGE badge.
//
// The on-stage scene's wing documents are a STALE SHADOW until the next
// park, deliberately: deleting them before the live documents exist
// would leave people nowhere if the cut failed halfway (v8.337.0).
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  parkStage,
  emptyScene,
  stageIsEmpty,
  planCut,
  refuseCut,
  travellerDefaults,
  resetScene,
  duplicateScene,
  cutLogLine,
  castDiff,
  sceneFromDoc,
  toSceneFile,
  readSceneFile,
  sceneFromFile,
  emptyStage,
  defaultMint as mint,
} from '../engine/scenes';
import { projectDefender } from '../engine/roster/projection';
import { centreMarker, deploy } from '../engine/battlefield/deployment';
import { terrainSolids } from '../engine/battlefield/presets';
import { groundAtFor } from './useBattlefieldSync';
import { tokenForSize } from '../engine/battlefield/tokens';
import downloadJson from '../utils/downloadJson';
import { copyOver } from '../engine/assets/holdings';

/** The rail's order: by `order`, then name. */
const byOrder = (a, b) => a.order - b.order || a.name.localeCompare(b.name);

/** The live table, as the engine needs to see it. */
const stageStateOf = (d) => ({
  roster: d.roster,
  tokens: d.tokens,
  terrain: d.terrain,
  tracker: d.trackerState,
  marker: d.deployment,
  arrivals: d.arrivals,
  hidden: d.hiddenMarks,
});

/**
 * Write a parked scene to the wings: terrain, cast, then the record.
 *
 * The record goes LAST so a listener never lists a scene whose parts are
 * not there yet. The previous terrain parts are read first so a shorter
 * map this time leaves no stale part behind to tear the next read.
 * Exported for the Assets page's session view (hooks/useSessionCopies),
 * which copies a library scene into the wings without this hook.
 */
export async function writeParked(mp, sessionId, parked, previousCastIds = []) {
  const { docIds } = await mp.readSceneTerrain(sessionId, parked.record.id);
  await mp.writeSceneTerrain(sessionId, parked.record.id, parked.terrain, docIds);
  await mp.writeSceneCast(
    sessionId,
    parked.record.id,
    parked.cast,
    castDiff(previousCastIds, parked.record.castIds).remove,
  );
  await mp.setScene(sessionId, parked.record);
}

/** A parked scene back from the wings, or null when its map is gone. */
async function readParked(mp, sessionId, record) {
  const [cast, { terrain }] = await Promise.all([
    mp.readSceneCast(sessionId, record.id, record.castIds),
    mp.readSceneTerrain(sessionId, record.id),
  ]);
  return terrain ? { record, cast, terrain } : null;
}

/**
 * Ring whoever the target has no mark for (`CutPlan.rings`): a traveller's team at its own point, other travellers at
 * the main marker; the target's own unmarked cast - and everyone, with no marker - at the arena's centre (a null
 * marker; plan scene-editor-library, B; 2026-10-28: the deployment area is the travellers' door only).
 */
function ringTravellers(plan, avoid, writeToken) {
  const { terrain } = plan;
  const { boundary } = terrain;
  const centre = centreMarker(boundary);
  const solids = terrainSolids(terrain);
  const groundAt = groundAtFor(terrain);
  for (const { marker, people } of plan.rings) {
    // From the rev each traveller's token holds now: `deploy` outranks it (CutPlan.heldRevs).
    const tokens = people.map((c) => tokenForSize(c.id, projectDefender(c)?.size, { rev: plan.heldRevs[c.id] ?? 0 }));
    const out = deploy(tokens, marker ?? centre, boundary, solids, avoid, groundAt);
    for (const token of Object.values(out.placed)) writeToken(token, { immediate: true });
  }
}

/**
 * HIDDEN BEFORE ANYTHING IS WRITTEN (plan map-display, G1): the target's
 * hidden cast are put back in the GM's hidden set first, so their tokens are
 * routed to their records and the roster push never sees them. A hidden
 * traveller is hidden already.
 */
function rehide(d, plan) {
  const already = d.hiddenMarks ?? {};
  const markOf = (id) => plan.entering.find((e) => e.combatant.id === id)?.token ?? null;
  const back = plan.roster
    .filter((c) => plan.hidden[c.id] && !already[c.id])
    .map((c) => ({ id: c.id, combatant: c, token: markOf(c.id), ...plan.hidden[c.id] }));
  if (back.length) d.restoreHidden?.(back);
}

/** Put the plan on the live table, in the order the header explains. Synchronous: React batches it into one render. */
function raise(d, plan) {
  for (const c of plan.leaving) {
    // At once, not an effect-tick later - a snapshot arriving in the gap
    // re-adds whoever it still lists (the recall's lesson, v8.30.0).
    d.removeToken(c.id);
    d.removeCombatant(c.id);
  }
  rehide(d, plan);
  for (const { token } of plan.entering) d.writeToken(token, { immediate: true });
  ringTravellers(plan, d.avoid, d.writeToken);
  // The map settles on the scene's own cast: the roster below is not set yet, and its objects' boxes carry riders.
  d.setTerrain(plan.terrain, { roster: plan.roster });
  d.setDeployment(plan.marker);
  d.setArrivals?.(plan.arrivals);
  d.setRoster(() => plan.roster);
  d.imposeTrackerState(plan.tracker);
  d.resetHistory();
}

const CANNOT_READ_MAP = "That scene's map could not be read.";
const ON_STAGE = 'That scene is on stage.';
const BACK_TO = 'That scene is the one Cut back returns to.';
const findScene = (scenes, id) => scenes.find((s) => s.id === id);
/** Only the fields given, so an absent one leaves what is there. */
const textPatch = ({ name, narration }) => ({
  ...(name !== undefined ? { name } : {}),
  ...(narration !== undefined ? { narration } : {}),
});

export default function useScenes(deps) {
  const { sessionId, isGm, mp, stage } = deps;
  // Read through a ref inside the async verbs: a cut spans awaits, and
  // the deps it closes over must be the ones of the render it runs in.
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const { scenes, setScenes, showRecord } = useSceneList(sessionId, isGm, mp);
  const onStage = useMemo(() => findScene(scenes, stage?.sceneId) ?? null, [scenes, stage?.sceneId]);
  const { busy, guarded } = useGuard();
  const ctx = { ...deps, depsRef, scenes, setScenes, showRecord, onStage, guarded };
  const cuts = useCuts(ctx);
  const text = useStageText(ctx);
  const wings = useWingVerbs(ctx);
  const defaults = useMemo(() => travellerDefaults(stage, deps.roster), [stage, deps.roster]);
  const stageSummary = useStageSummary(deps.roster, deps.trackerState);
  return { scenes, onStage, busy, travellerDefaults: defaults, stageSummary, ...cuts, ...text, ...wings };
}

/** The scene records (GM only), in rail order, and a way to show one before the listener confirms it. */
function useSceneList(sessionId, isGm, mp) {
  const [scenes, setScenes] = useState([]);
  useEffect(() => {
    if (!sessionId || !isGm) return undefined;
    return mp.onScenesUpdate(sessionId, (records) =>
      setScenes(records.map(sceneFromDoc).filter(Boolean).sort(byOrder)),
    );
  }, [sessionId, isGm, mp]);
  /** Show a record at once; the listener confirms it. */
  const showRecord = useCallback((record) => {
    setScenes((prev) => [...prev.filter((s) => s.id !== record.id), record].sort(byOrder));
  }, []);
  return { scenes, setScenes, showRecord };
}

/** One verb at a time: a second cut mid-cut would park a half-raised stage. */
function useGuard() {
  const [busy, setBusy] = useState(false);
  const guarded = useCallback(
    async (work) => {
      if (busy) return 'Another scene change is still in progress.';
      setBusy(true);
      try {
        return await work();
      } finally {
        setBusy(false);
      }
    },
    [busy],
  );
  return { busy, guarded };
}

/** What the ON STAGE card says - read off the live table, since the on-stage record is not authoritative. */
function useStageSummary(roster, trackerState) {
  return useMemo(() => {
    const t = trackerState;
    return {
      count: roster.length,
      round: t?.round ?? 1,
      turn: t?.active ? (t.participants[t.activeIndex]?.name ?? null) : null,
    };
  }, [roster.length, trackerState]);
}

/**
 * The record the stage is parked into: the one it is named as, or a new
 * one for an unnamed stage worth keeping. Null when nothing is worth
 * parking - an unnamed, empty table.
 */
function recordToPark(onStage, d, scenes, parkAs, now) {
  if (onStage) return onStage;
  if (stageIsEmpty(stageStateOf(d))) return null;
  return emptyScene(mint('scene'), parkAs || `Scene ${scenes.length + 1}`, d.terrain, null, scenes.length, now);
}

/** Park the stage into `from`; the sentence that stops the cut, or null. */
async function parkInto(from, d, travellers, now, { mp, sessionId, showRecord }) {
  try {
    const parked = parkStage(stageStateOf(d), from, now, travellers);
    await writeParked(mp, sessionId, parked, from.castIds);
    showRecord(parked.record);
    return null;
  } catch (err) {
    return `Could not park the stage: ${err?.message ?? err}`;
  }
}

/** The stage document a cut raises. */
const stageAfterCut = (target, travellers, curtain, from, now) => ({
  sceneId: target.record.id,
  name: target.record.name,
  narration: target.record.narration,
  curtain,
  // Every member reads this document: a hidden traveller is never in it.
  travellers: [...travellers],
  previousSceneId: from?.id ?? null,
  cutAt: now,
});

/** Cutting: the refusal, the cut itself, and the verbs that start one. */
function useCuts(ctx) {
  const { mp, sessionId, depsRef, scenes, stage, onStage, showRecord, setStage, narrate } = ctx;
  const rollFor = useCallback((c) => depsRef.current.participantFor(c.name, c.id, c.sheet), [depsRef]);
  const canCut = useCanCut(depsRef, scenes, stage?.sceneId);
  /** The cut itself: park, plan, raise, announce. Returns the sentence that stopped it, or null. */
  const performCut = useCallback(
    async (target, travellers, { curtain = false, parkAs = '' } = {}) => {
      const d = depsRef.current;
      const now = Date.now();
      const from = recordToPark(onStage, d, scenes, parkAs, now);
      const failed = from && (await parkInto(from, d, travellers, now, { mp, sessionId, showRecord }));
      if (failed) return failed;
      const plan = planCut(stageStateOf(depsRef.current), target, travellers, rollFor, d.admit);
      raise(depsRef.current, plan);
      // A scene may open in a phase of its own. After the raise (its people
      // are on the table) and before the stage document (the commit point
      // every client reacts to), so the title card comes up in the right livery.
      if (target.record.phase) depsRef.current.setPhase?.(target.record.phase);
      // The public words of the cut name nobody hidden (plan map-display, G1).
      const seen = new Set([...travellers].filter((id) => !plan.hidden[id]));
      setStage(stageAfterCut(target, seen, curtain, from, now));
      const names = plan.roster.filter((c) => seen.has(c.id)).map((c) => c.name);
      narrate?.(cutLogLine(from?.name ?? null, target.record.name, names));
      return null;
    },
    [depsRef, onStage, scenes, mp, sessionId, rollFor, setStage, showRecord, narrate],
  );
  const { cutTo, cutBack } = useCutTo({ ...ctx, canCut, performCut });
  const newScene = useNewScene({ ...ctx, performCut });
  return { canCut, cutTo, cutBack, newScene };
}

/** The sentence that stops a cut, or null. */
function useCanCut(depsRef, scenes, stageSceneId) {
  return useCallback(
    (sceneId, travellers) => {
      const d = depsRef.current;
      const target = findScene(scenes, sceneId);
      const names = Object.fromEntries(d.roster.map((c) => [c.id, c.name]));
      return refuseCut({
        pendingPrompts: d.pendingPrompts,
        stageSceneId: stageSceneId ?? null,
        targetId: sceneId,
        targetCastIds: target?.castIds ?? [],
        travellers,
        names,
      });
    },
    [depsRef, scenes, stageSceneId],
  );
}

/** Cut to a scene in the wings, or back to the one before. */
function useCutTo({ guarded, canCut, scenes, mp, sessionId, performCut, stage }) {
  const cutTo = useCallback(
    (sceneId, travellers) =>
      guarded(async () => {
        const refusal = canCut(sceneId, travellers);
        if (refusal) return refusal;
        const record = findScene(scenes, sceneId);
        if (!record) return 'That scene is gone.';
        const target = await readParked(mp, sessionId, record);
        if (!target) return CANNOT_READ_MAP;
        return performCut(target, travellers);
      }),
    [guarded, canCut, scenes, mp, sessionId, performCut],
  );
  const cutBack = useCallback(
    (travellers) =>
      stage?.previousSceneId ? cutTo(stage.previousSceneId, travellers) : Promise.resolve('Nothing to cut back to.'),
    [stage?.previousSceneId, cutTo],
  );
  return { cutTo, cutBack };
}

/** Park the stage and raise an empty one on `terrain`, curtain down. */
function useNewScene({ guarded, scenes, mp, sessionId, showRecord, performCut }) {
  return useCallback(
    ({ terrain, mapName = null, name, travellers = new Set(), parkAs = '' }) =>
      guarded(async () => {
        const at = scenes.length;
        const record = emptyScene(mint('scene'), name || `Scene ${at + 1}`, terrain, mapName, at, Date.now());
        await mp.setScene(sessionId, record);
        showRecord(record);
        return performCut({ record, cast: [], terrain }, travellers, { curtain: true, parkAs });
      }),
    [guarded, scenes.length, mp, sessionId, showRecord, performCut],
  );
}

/** Naming the stage, its curtain and its text, and renaming a scene. */
function useStageText({ guarded, depsRef, onStage, scenes, stage, mp, sessionId, showRecord, setStage }) {
  const strike = useStrike({ guarded, depsRef, onStage, scenes, mp, sessionId, showRecord, setStage });
  const setCurtain = useCallback((curtain) => setStage((s) => ({ ...(s ?? emptyStage()), curtain })), [setStage]);
  /** The name and the line under it - on the stage document, and on the record so it survives a cut away. */
  const setStageText = useCallback(
    (text) => {
      setStage((s) => ({ ...(s ?? emptyStage()), ...textPatch(text) }));
      if (!onStage) return;
      const record = { ...onStage, ...textPatch(text) };
      showRecord(record);
      mp.setScene(sessionId, record).catch(console.error);
    },
    [setStage, onStage, showRecord, mp, sessionId],
  );
  const rename = useCallback(
    (sceneId, name) => {
      const record = findScene(scenes, sceneId);
      if (!record) return;
      if (record.id === stage?.sceneId) return setStageText({ name });
      showRecord({ ...record, name });
      mp.setScene(sessionId, { ...record, name }).catch(console.error);
    },
    [scenes, stage?.sceneId, setStageText, showRecord, mp, sessionId],
  );
  /** The phase the scene on stage opens in, or null for "as the table is". Written to its record. */
  const setScenePhase = useCallback(
    (phase) => {
      if (!onStage) return;
      const record = { ...onStage, phase };
      showRecord(record);
      mp.setScene(sessionId, record).catch(console.error);
    },
    [onStage, showRecord, mp, sessionId],
  );
  return { strike, setCurtain, setStageText, setScenePhase, rename };
}

/** Name the stage as a scene. Nothing moves: the live documents stay live. */
function useStrike({ guarded, depsRef, onStage, scenes, mp, sessionId, showRecord, setStage }) {
  return useCallback(
    (name) =>
      guarded(async () => {
        const d = depsRef.current;
        const record = onStage
          ? { ...onStage, name }
          : {
              ...emptyScene(mint('scene'), name, d.terrain, null, scenes.length, Date.now()),
              castIds: d.roster.map((c) => c.id),
            };
        await mp.setScene(sessionId, record);
        showRecord(record);
        setStage((s) => ({
          ...(s ?? emptyStage()),
          sceneId: record.id,
          name: record.name,
          narration: record.narration,
          // Every display flashes the set's outline: it is a scene now (plan map-display, I). A rename is not.
          ...(onStage ? {} : { struckAt: Date.now() }),
        }));
        return null;
      }),
    [guarded, depsRef, onStage, scenes.length, mp, sessionId, showRecord, setStage],
  );
}

/** Delete a scene in the wings: cast, terrain, then the record. */
async function deleteParked(mp, sessionId, record) {
  const { docIds } = await mp.readSceneTerrain(sessionId, record.id);
  await mp.deleteSceneCast(sessionId, record.id, record.castIds);
  await mp.deleteSceneTerrain(sessionId, record.id, docIds);
  await mp.deleteScene(sessionId, record.id);
}

/** A scene in the wings with its cast fresh again. */
async function resetParked(mp, sessionId, record, showRecord) {
  const parked = await readParked(mp, sessionId, record);
  if (!parked) return CANNOT_READ_MAP;
  const clean = resetScene(parked);
  await mp.writeSceneCast(sessionId, record.id, clean.cast, []);
  await mp.setScene(sessionId, clean.record);
  showRecord(clean.record);
  return null;
}

const sceneFileName = (record) => `${record.name.replace(/[^\w-]+/g, '_') || 'scene'}.scene.json`;

/** The verbs over the wings: delete, reset, duplicate, export, import. */
function useWingVerbs(ctx) {
  const { guarded, scenes, stage, mp, sessionId, showRecord, setScenes, depsRef } = ctx;
  /** A verb on one wing record: a missing record is a quiet no-op. */
  const onRecord = useCallback(
    (sceneId, work) =>
      guarded(async () => {
        const record = findScene(scenes, sceneId);
        return record ? work(record) : null;
      }),
    [guarded, scenes],
  );
  /** A parked scene as it stands: read from the wings, or a snapshot of the stage when it is the one on. */
  const parkedFor = useCallback(
    async (record) => {
      if (record.id !== stage?.sceneId) return readParked(mp, sessionId, record);
      return parkStage(stageStateOf(depsRef.current), record, Date.now());
    },
    [stage?.sceneId, mp, sessionId, depsRef],
  );
  const offStage = useOffStageVerbs({ onRecord, stage, mp, sessionId, showRecord, setScenes });
  const copies = useCopyVerbs({ onRecord, parkedFor, scenes, mp, sessionId, showRecord });
  const importSceneFile = useImportSceneFile({ guarded, scenes, mp, sessionId, showRecord });
  const fromLibrary = useLibraryCopies({ guarded, onRecord, scenes, stage, mp, sessionId, showRecord });
  const saveToLibrary = useSaveToLibrary({ onRecord, parkedFor, mp, sessionId, showRecord });
  return { ...offStage, ...copies, importSceneFile, ...fromLibrary, saveToLibrary };
}

/**
 * Save a scene to the GM's asset library (plan assets-library, stage 2):
 * the scene as it stands - the live table when it is the one on stage - is
 * handed to `save`, which writes the library's version and resolves with
 * its origin; the record then points at that version, so the GM's own save
 * never reads as "newer". `save` throws its own sentence; nothing is written
 * here until it has landed.
 */
function useSaveToLibrary({ onRecord, parkedFor, mp, sessionId, showRecord }) {
  return useCallback(
    (sceneId, save) =>
      onRecord(sceneId, async (record) => {
        const parked = await parkedFor(record);
        if (!parked) return CANNOT_READ_MAP;
        const origin = await save(parked);
        const next = { ...record, origin };
        await mp.setScene(sessionId, next);
        showRecord(next);
        return null;
      }),
    [onRecord, parkedFor, mp, sessionId, showRecord],
  );
}

/**
 * A scene from the GM's asset library, COPIED into the wings (plan
 * assets-library, stage 2): `parked` is the asset's version read with fresh
 * ids, `origin` which asset and version it is. `parkFromLibrary` adds a LIST of
 * them (`{ parked, origin }`, several picked at once) at the end of the rail, in
 * the order given - one guarded verb, so each takes the next place; `replaceFromLibrary` brings a copy up to a newer
 * version IN PLACE - the same id, name and place in the rail, the old cast
 * and terrain written over - and refuses the scene on stage (it is the
 * table) and the one Cut back returns to (its marks are how the travellers
 * go home).
 */
function useLibraryCopies({ guarded, onRecord, scenes, stage, mp, sessionId, showRecord }) {
  const parkFromLibrary = useCallback(
    (copies) =>
      guarded(async () => {
        for (const [i, { parked, origin }] of copies.entries()) {
          const copy = { ...parked, record: { ...parked.record, order: scenes.length + i, origin } };
          await writeParked(mp, sessionId, copy);
          showRecord(copy.record);
        }
        return null;
      }),
    [guarded, scenes.length, mp, sessionId, showRecord],
  );
  const replaceFromLibrary = useCallback(
    (sceneId, parked, origin) =>
      onRecord(sceneId, async (record) => {
        if (record.id === stage?.sceneId) return ON_STAGE;
        if (record.id === stage?.previousSceneId) return BACK_TO;
        const next = copyOver(record, parked, origin);
        await writeParked(mp, sessionId, next, record.castIds);
        showRecord(next.record);
        return null;
      }),
    [onRecord, stage?.sceneId, stage?.previousSceneId, mp, sessionId, showRecord],
  );
  return { parkFromLibrary, replaceFromLibrary };
}

/** Only a scene in the wings: the one on stage is the table, and the table is not deleted or reset. */
function useOffStageVerbs({ onRecord, stage, mp, sessionId, showRecord, setScenes }) {
  const remove = useCallback(
    (sceneId) =>
      onRecord(sceneId, async (record) => {
        if (record.id === stage?.sceneId) return ON_STAGE;
        await deleteParked(mp, sessionId, record);
        setScenes((prev) => prev.filter((s) => s.id !== sceneId));
        return null;
      }),
    [onRecord, stage?.sceneId, mp, sessionId, setScenes],
  );
  const reset = useCallback(
    (sceneId) =>
      onRecord(sceneId, (record) =>
        record.id === stage?.sceneId ? ON_STAGE : resetParked(mp, sessionId, record, showRecord),
      ),
    [onRecord, stage?.sceneId, mp, sessionId, showRecord],
  );
  return { remove, reset };
}

/** Copies of a scene as it stands: a duplicate in the wings, or a file to save. */
function useCopyVerbs({ onRecord, parkedFor, scenes, mp, sessionId, showRecord }) {
  const duplicate = useCallback(
    (sceneId) =>
      onRecord(sceneId, async (record) => {
        const parked = await parkedFor(record);
        if (!parked) return CANNOT_READ_MAP;
        const copy = duplicateScene(parked, mint, Date.now(), scenes.length);
        await writeParked(mp, sessionId, copy);
        showRecord(copy.record);
        return null;
      }),
    [onRecord, parkedFor, scenes.length, mp, sessionId, showRecord],
  );
  const exportScene = useCallback(
    (sceneId) =>
      onRecord(sceneId, async (record) => {
        const parked = await parkedFor(record);
        if (!parked) return CANNOT_READ_MAP;
        const file = toSceneFile(parked, { includeState: true, includeNarrative: false }, new Date().toISOString());
        downloadJson(file, sceneFileName(record));
        return null;
      }),
    [onRecord, parkedFor],
  );
  return { duplicate, exportScene };
}

/** A parsed file into the wings. Returns `{ error }` or `{ name, warnings }`. */
function useImportSceneFile({ guarded, scenes, mp, sessionId, showRecord }) {
  return useCallback(
    (data) =>
      guarded(async () => {
        const { scene, warnings, error } = readSceneFile(data);
        if (error) return { error };
        const parked = sceneFromFile(scene, mint, Date.now(), scenes.length);
        await writeParked(mp, sessionId, parked);
        showRecord(parked.record);
        return { name: parked.record.name, warnings };
      }),
    [guarded, scenes.length, mp, sessionId, showRecord],
  );
}
